import type { TeamState } from '@timato/shared';
import { db } from '@/db/db';

/**
 * Shared projects, tasks and labels are team-plane rows: fetched, then mirrored into Dexie (with `teamId` set) so the
 * Projects tab, the goal combobox and pickers show them next to personal rows. Writes never go through the outbox.
 */
export async function mirrorTeamState(state: TeamState) {
  const teamId = state.team.id;
  await db.transaction('rw', [db.projects, db.tasks, db.labels], async () => {
    const [oldP, oldL] = await Promise.all([db.projects.where('teamId').equals(teamId).primaryKeys(), db.labels.where('teamId').equals(teamId).primaryKeys()]);
    const oldT = (await db.tasks.toArray()).filter((t) => oldP.includes(t.projectId)).map((t) => t.id);
    const keepP = new Set(state.projects.map((p) => p.id)), keepT = new Set(state.tasks.map((t) => t.id)), keepL = new Set(state.labels.map((l) => l.id));
    await db.projects.bulkDelete(oldP.filter((id) => !keepP.has(id as string)));
    await db.tasks.bulkDelete(oldT.filter((id) => !keepT.has(id)));
    await db.labels.bulkDelete(oldL.filter((id) => !keepL.has(id as string)));
    await db.projects.bulkPut(state.projects);
    await db.tasks.bulkPut(state.tasks);
    await db.labels.bulkPut(state.labels);
  });
}

/** Drop every mirrored row when the user leaves the team or signs out. */
export async function clearTeamMirror(teamId: string) {
  const projectIds = await db.projects.where('teamId').equals(teamId).primaryKeys();
  await db.transaction('rw', [db.projects, db.tasks, db.labels], async () => {
    const taskIds = (await db.tasks.toArray()).filter((t) => projectIds.includes(t.projectId)).map((t) => t.id);
    await db.tasks.bulkDelete(taskIds);
    await db.projects.bulkDelete(projectIds);
    await db.labels.bulkDelete(await db.labels.where('teamId').equals(teamId).primaryKeys());
  });
}

/** The team a project belongs to, if any (sessions inherit it). */
export const teamIdForProject = async (projectId: string | null) => (projectId ? (await db.projects.get(projectId))?.teamId ?? null : null);
